import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ConfirmDialog from '@/components/ui/ConfirmDialog';

type ConfirmVariant = 'primary' | 'secondary' | 'outline' | 'danger' | 'warning' | 'link';

type ConfirmOptions = {
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: ConfirmVariant;
  onConfirm?: () => Promise<void> | void;
};

export const useConfirmDialog = () => {
  const [state, setState] = useState<{ options: ConfirmOptions | null; isOpen: boolean; isProcessing: boolean }>({
    options: null,
    isOpen: false,
    isProcessing: false,
  });
  const resolverRef = useRef<(result: boolean) => void>(undefined);
  const rejecterRef = useRef<(reason?: unknown) => void>(undefined);
  const closeTimeoutRef = useRef<number>(undefined);

  const closeDialog = useCallback((result: boolean) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    resolverRef.current?.(result);
    resolverRef.current = undefined;
    rejecterRef.current = undefined;
    setState((prev) => ({ ...prev, isOpen: false, isProcessing: false }));

    // Keep dialog content during close animation to avoid flashing defaults
    closeTimeoutRef.current = window.setTimeout(() => {
      setState({ options: null, isOpen: false, isProcessing: false });
    }, 320);
  }, []);

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve, reject) => {
        if (resolverRef.current) {
          resolverRef.current(false);
        }
        resolverRef.current = resolve;
        rejecterRef.current = reject;
        setState({ options: opts, isOpen: true, isProcessing: false });
      }),
    []
  );

  const handleCancel = useCallback(() => closeDialog(false), [closeDialog]);
  const handleConfirm = useCallback(async () => {
    if (!state.options || state.isProcessing) return;

    if (state.options.onConfirm) {
      setState((prev) => ({ ...prev, isProcessing: true }));
      try {
        await state.options.onConfirm();
        closeDialog(true);
      } catch (err) {
        setState((prev) => ({ ...prev, isProcessing: false }));
        rejecterRef.current?.(err);
      }
      return;
    }

    closeDialog(true);
  }, [state.options, state.isProcessing, closeDialog]);

  const dialog = useMemo(
    () => (
      <ConfirmDialog
        isOpen={state.isOpen}
        title={state.options?.title}
        message={state.options?.message ?? ''}
        confirmLabel={state.options?.confirmLabel}
        cancelLabel={state.options?.cancelLabel}
        confirmVariant={state.options?.confirmVariant}
        isProcessing={state.isProcessing}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    ),
    [state, handleCancel, handleConfirm]
  );

  useEffect(
    () => () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    },
    []
  );

  return {
    confirm,
    dialog,
  };
};
